import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from './useAuth';

/**
 * Login page with StratMined branding.
 * Redirects authenticated users to the dashboard.
 */
export const LoginPage: React.FC = () => {
  const { isAuthenticated, login, isLoading } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-[#1B2A4A] flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 25% 25%, #C5922E 1px, transparent 1px), radial-gradient(circle at 75% 75%, #C5922E 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Login card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Top accent */}
          <div className="h-1.5 bg-gradient-to-r from-[#C5922E] via-[#C8963E] to-[#C5922E]" />

          <div className="p-10">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="flex flex-col items-center">
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 64 64"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Stacked layers icon */}
                  <path
                    d="M32 8L4 24L32 40L60 24L32 8Z"
                    fill="#C5922E"
                    fillOpacity="0.9"
                  />
                  <path
                    d="M4 32L32 48L60 32"
                    stroke="#C5922E"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4 40L32 56L60 40"
                    stroke="#C8963E"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* SM text */}
                  <text
                    x="32"
                    y="28"
                    textAnchor="middle"
                    fill="#1B2A4A"
                    fontFamily="'Playfair Display', serif"
                    fontSize="14"
                    fontWeight="700"
                  >
                    SM
                  </text>
                </svg>
                <div className="mt-2 text-[10px] tracking-[0.35em] text-[#C5922E] font-semibold uppercase">
                  Intelligence
                </div>
              </div>
            </div>

            {/* Title */}
            <h1
              className="text-center text-2xl font-semibold text-[#1B2A4A] mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              StratMined Intelligence Portal
            </h1>
            <p className="text-center text-sm text-[#6B7280] mb-8">
              Sign in to access mining intelligence
            </p>

            {/* Tagline */}
            <div className="text-center mb-8">
              <span className="text-xs tracking-widest text-[#C5922E] uppercase font-medium">
                Strategic Mining Intelligence for Africa
              </span>
            </div>

            {/* Sign In Button */}
            <button
              onClick={login}
              disabled={isLoading}
              className="w-full py-3.5 px-6 rounded-lg font-medium text-white text-sm tracking-wide
                         bg-gradient-to-r from-[#C5922E] to-[#A87A22]
                         hover:from-[#A87A22] hover:to-[#8B6235]
                         focus:outline-none focus:ring-2 focus:ring-[#C5922E] focus:ring-offset-2
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-all duration-200 shadow-md hover:shadow-lg
                         transform hover:-translate-y-0.5"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Authenticating...</span>
                </div>
              ) : (
                'Sign in with your email'
              )}
            </button>

            {/* Registration link */}
            <div className="mt-5 text-center">
              <p className="text-xs text-[#6B7280]">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#C5922E] font-medium hover:underline">
                  Create one here
                </Link>
              </p>
            </div>

            {/* Footer */}
            <p className="text-center text-xs text-[#6B7280] mt-4">
              Secure authentication by StratMined
            </p>
          </div>
        </div>

        {/* Bottom text */}
        <p className="text-center text-xs text-[#6B7280]/50 mt-6">
          &copy; {new Date().getFullYear()} StratMined Intelligence. All rights reserved.
        </p>
      </div>
    </div>
  );
};
